import React, { useState, useMemo } from "react";
import { useMembers, AdminMember } from "../hooks/useMembers";
import { logSms } from "../lib/smsLog";

const AYLAR = ["", "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const DEFAULT_MSG = `Sayın {AD}, dogum gununuzu en icten dileklerimizle kutluyor, saglik ve mutluluk dolu nice yillar diliyoruz. Bursa Trabzonlular Dernegi`;

const BirthdaysPage: React.FC = () => {
  const { members, loading, error } = useMembers();
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1);
  const [sending, setSending] = useState<string | null>(null);
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [smsTemplate, setSmsTemplate] = useState(DEFAULT_MSG);

  // birthDate'i olmayan üyeler (dogumAy null) doğum günü listelerinde GÖRÜNMESİN.
  const todayBirthdays = useMemo(() =>
    members.filter(m => m.dogumAy === today.getMonth() + 1 && m.dogumGun === today.getDate()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members]
  );

  const monthBirthdays = useMemo(() =>
    members
      .filter(m => m.dogumAy === viewMonth)
      .sort((a, b) => (a.dogumGun ?? 0) - (b.dogumGun ?? 0)),
    [members, viewMonth]
  );

  const buildMsg = (adSoyad: string) =>
    smsTemplate.replace("{AD}", adSoyad.split(" ")[0]);

  const sendSms = async (member: AdminMember, message: string): Promise<boolean> => {
    try {
      // SMS gönderim (Netgsm) — ŞU AN KULLANILMIYOR ama altyapı korundu.
      const res = await fetch("/.netlify/functions/send-sms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phones: [member.telefon], message }),
      });
      const data = await res.json();
      return res.ok && data.success;
    } catch {
      return false;
    }
  };

  const handleSendOne = async (member: AdminMember) => {
    if (!member.telefon) return;
    setSending(member.id);
    setResult(null);
    const msg = buildMsg(member.adSoyad);
    const ok = await sendSms(member, msg);
    logSms({ alici: member.telefon, aliciAd: member.adSoyad, mesaj: msg, durum: ok ? "gönderildi" : "hata" });
    if (ok) setSent(prev => new Set(prev).add(member.id));
    setResult({ ok, msg: ok ? `${member.adSoyad} kişisine doğum günü SMS'i gönderildi.` : "SMS gönderilemedi." });
    setSending(null);
  };

  const handleBulkToday = async () => {
    if (!todayBirthdays.length) return;
    setBulkSending(true);
    setResult(null);
    let successCount = 0;
    for (const member of todayBirthdays) {
      if (!member.telefon) continue;
      const msg = buildMsg(member.adSoyad);
      const ok = await sendSms(member, msg);
      logSms({ alici: member.telefon, aliciAd: member.adSoyad, mesaj: msg, durum: ok ? "gönderildi" : "hata" });
      if (ok) { successCount++; setSent(prev => new Set(prev).add(member.id)); }
    }
    setResult({ ok: true, msg: `${successCount}/${todayBirthdays.length} kişiye doğum günü SMS'i gönderildi.` });
    setBulkSending(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-gray-500 py-20 justify-center">
        <i className="fa-solid fa-circle-notch fa-spin text-2xl text-[#8B1428]" /> Üyeler yükleniyor...
      </div>
    );
  }
  if (error) {
    return <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">Üyeler yüklenemedi.</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">Doğum Günleri</h2>
        <p className="text-sm text-gray-500 mt-0.5">Üyelerin doğum günlerini görüntüleyin ve tebrik SMS'i gönderin</p>
      </div>

      {members.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-cake-candles text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500">Üye bulunamadı</p>
        </div>
      ) : (
        <>
          {/* SMS Şablonu — düzenlenebilir */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <h3 className="font-bold text-gray-700 mb-1 flex items-center gap-2">
              <i className="fa-solid fa-message text-[#8B1428]" />
              Doğum Günü SMS İçeriği
            </h3>
            <p className="text-xs text-gray-400 mb-3">
              <code className="bg-gray-100 px-1 rounded">{"{AD}"}</code> ifadesi gönderim sırasında kişinin adıyla değiştirilir.
            </p>
            <textarea
              rows={3}
              value={smsTemplate}
              onChange={e => setSmsTemplate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">{smsTemplate.length} karakter</span>
              <button onClick={() => setSmsTemplate(DEFAULT_MSG)}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                <i className="fa-solid fa-rotate-left mr-1" />Varsayılana sıfırla
              </button>
            </div>
            <div className="mt-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
              <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Örnek Önizleme</p>
              <p className="text-sm text-gray-700 leading-relaxed">{buildMsg("Ahmet")}</p>
            </div>
          </div>

          {/* Bugünün doğum günleri */}
          <div className="bg-gradient-to-r from-pink-50 to-red-50 border border-pink-200 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                  <i className="fa-solid fa-cake-candles text-pink-500 text-xl" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800">Bugün Doğan Üyeler</h3>
                  <p className="text-sm text-gray-500">
                    {today.getDate()} {AYLAR[today.getMonth() + 1]} {today.getFullYear()} —{" "}
                    {todayBirthdays.length > 0
                      ? `${todayBirthdays.length} üyenin doğum günü!`
                      : "Bugün doğan üye yok."}
                  </p>
                </div>
              </div>
              {todayBirthdays.length > 0 && (
                <button onClick={handleBulkToday} disabled={bulkSending}
                  className="flex items-center gap-2 text-white text-sm font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg, #8B1428, #0F4C81)" }}>
                  {bulkSending
                    ? <><i className="fa-solid fa-circle-notch fa-spin" /> Gönderiliyor...</>
                    : <><i className="fa-solid fa-paper-plane" /> Hepsine SMS Gönder</>}
                </button>
              )}
            </div>

            {todayBirthdays.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {todayBirthdays.map(m => (
                  <div key={m.id} className="flex items-center gap-2 bg-white border border-pink-200 rounded-full px-3 py-1.5 text-sm">
                    <i className="fa-solid fa-user text-pink-400 text-xs" />
                    <span className="font-medium text-gray-700">{m.adSoyad}</span>
                    {sent.has(m.id) && <i className="fa-solid fa-check text-green-500 text-xs" />}
                  </div>
                ))}
              </div>
            )}

            {result && (
              <div className={`mt-4 text-sm flex items-center gap-2 px-4 py-3 rounded-lg
                ${result.ok ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
                <i className={`fa-solid ${result.ok ? "fa-circle-check" : "fa-triangle-exclamation"}`} />
                {result.msg}
              </div>
            )}
          </div>

          {/* Aylık doğum günleri */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <h3 className="font-bold text-gray-800">Aylık Doğum Günü Listesi</h3>
              <div className="flex items-center gap-2">
                <button onClick={() => setViewMonth(m => m === 1 ? 12 : m - 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600">
                  <i className="fa-solid fa-chevron-left text-xs" />
                </button>
                <span className="font-semibold text-gray-700 min-w-[100px] text-center">{AYLAR[viewMonth]}</span>
                <button onClick={() => setViewMonth(m => m === 12 ? 1 : m + 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600">
                  <i className="fa-solid fa-chevron-right text-xs" />
                </button>
              </div>
            </div>

            {monthBirthdays.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-8">Bu ayda doğan üye bulunamadı.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="py-2 text-left text-xs font-semibold text-gray-500 uppercase">Ad Soyad</th>
                      <th className="py-2 text-left text-xs font-semibold text-gray-500 uppercase">Doğum Günü</th>
                      <th className="py-2 text-left text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Telefon</th>
                      <th className="py-2 text-left text-xs font-semibold text-gray-500 uppercase">Önizleme & SMS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {monthBirthdays.map(m => {
                      const isToday = m.dogumGun === today.getDate() && m.dogumAy === today.getMonth() + 1;
                      return (
                        <tr key={m.id} className={isToday ? "bg-pink-50" : "hover:bg-gray-50"}>
                          <td className="py-3 font-medium text-gray-800">
                            <div className="flex items-center gap-2">
                              {isToday && <i className="fa-solid fa-cake-candles text-pink-400 text-xs" />}
                              {m.adSoyad}
                            </div>
                          </td>
                          <td className="py-3 text-gray-600">{m.dogumGun} {AYLAR[viewMonth]}</td>
                          <td className="py-3 text-gray-500 font-mono text-xs hidden sm:table-cell">{m.telefon || "—"}</td>
                          <td className="py-3">
                            {m.telefon ? (
                              sent.has(m.id) ? (
                                <span className="text-green-600 text-xs font-semibold flex items-center gap-1">
                                  <i className="fa-solid fa-check" /> Gönderildi
                                </span>
                              ) : (
                                <div className="flex flex-col gap-1">
                                  <p className="text-xs text-gray-400 italic leading-snug max-w-xs">
                                    {buildMsg(m.adSoyad).slice(0, 60)}…
                                  </p>
                                  <button
                                    onClick={() => handleSendOne(m)}
                                    disabled={sending === m.id}
                                    className="flex items-center gap-1 text-[#8B1428] hover:text-[#6B1020] text-xs font-semibold disabled:opacity-50 w-fit">
                                    {sending === m.id
                                      ? <><i className="fa-solid fa-circle-notch fa-spin" /> Gönderiliyor</>
                                      : <><i className="fa-solid fa-paper-plane" /> Gönder</>}
                                  </button>
                                </div>
                              )
                            ) : (
                              <span className="text-gray-300 text-xs">Telefon yok</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BirthdaysPage;
